/**
 * In-deal scenario versions — named assumption/input copies on one file.
 * Not separate deals. Legacy records without scenarios hydrate as "Base case".
 */

export const BASE_CASE_NAME = 'Base case';

export function deepClone(value) {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
}

export function newScenarioId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return `scen${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeName(name, fallback = BASE_CASE_NAME) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed || fallback;
}

export function createScenario({
  name = BASE_CASE_NAME,
  inputs = {},
  assumptions = {},
  ordinal = 1,
  id,
} = {}) {
  return {
    id: id || newScenarioId(),
    name: normalizeName(name),
    ordinal: Number.isFinite(ordinal) ? ordinal : 1,
    inputs: deepClone(inputs) || {},
    assumptions: deepClone(assumptions) || {},
  };
}

/**
 * Normalize a deal / autosave record into a scenarios list + active id.
 * Existing deals with no versions become a single Base case from current inputs.
 */
export function hydrateScenarios(record) {
  const rawList = Array.isArray(record?.scenarios) ? record.scenarios : [];
  const usable = rawList.filter((s) => s && typeof s === 'object');

  if (usable.length === 0) {
    const base = createScenario({
      name: BASE_CASE_NAME,
      inputs: record?.inputs || {},
      assumptions: record?.assumptions || {},
      ordinal: 1,
    });
    return { scenarios: [base], activeScenarioId: base.id };
  }

  const scenarios = usable.map((s, i) => createScenario({
    id: typeof s.id === 'string' && s.id.trim() ? s.id : newScenarioId(),
    name: s.name,
    ordinal: Number.isFinite(s.ordinal) ? s.ordinal : i + 1,
    inputs: s.inputs != null ? s.inputs : record?.inputs,
    assumptions: s.assumptions != null ? s.assumptions : record?.assumptions,
  }));

  const requested = typeof record?.activeScenarioId === 'string' ? record.activeScenarioId : null;
  const active = scenarios.find((s) => s.id === requested) || preferredScenario(scenarios);
  return { scenarios, activeScenarioId: active.id };
}

export function getActiveScenario(scenarios, activeScenarioId) {
  if (!Array.isArray(scenarios) || scenarios.length === 0) return null;
  return scenarios.find((s) => s.id === activeScenarioId) || preferredScenario(scenarios);
}

export function findScenario(scenarios, id) {
  if (!Array.isArray(scenarios) || !id) return null;
  return scenarios.find((s) => s.id === id) || null;
}

/** Lowest ordinal (then id) — used when the active row is deleted or missing. */
export function preferredScenario(scenarios) {
  if (!Array.isArray(scenarios) || scenarios.length === 0) return null;
  return [...scenarios].sort((a, b) => {
    const oa = Number(a.ordinal) || 0;
    const ob = Number(b.ordinal) || 0;
    if (oa !== ob) return oa - ob;
    return String(a.id || '').localeCompare(String(b.id || ''));
  })[0];
}

/**
 * Flush the current workspace into `currentId`, then load `nextId`.
 * Exact id match only — never fall back to version[0] (that copies
 * the leaving version’s numbers onto the first row).
 */
export function activateScenario(scenarios, currentId, nextId, currentInputs, currentAssumptions) {
  const flushed = syncActiveScenario(scenarios, currentId, currentInputs, currentAssumptions);
  if (!nextId || nextId === currentId) {
    const current = findScenario(flushed, currentId) || flushed[0] || null;
    return {
      scenarios: flushed,
      activeScenarioId: current?.id || currentId,
      inputs: deepClone(current?.inputs || currentInputs) || {},
      assumptions: deepClone(current?.assumptions || currentAssumptions) || {},
      switched: false,
    };
  }
  const next = findScenario(flushed, nextId);
  if (!next) {
    const current = findScenario(flushed, currentId) || flushed[0] || null;
    return {
      scenarios: flushed,
      activeScenarioId: current?.id || currentId,
      inputs: deepClone(currentInputs) || {},
      assumptions: deepClone(currentAssumptions) || {},
      switched: false,
    };
  }
  return {
    scenarios: flushed,
    activeScenarioId: next.id,
    inputs: deepClone(next.inputs) || {},
    assumptions: deepClone(next.assumptions) || {},
    switched: true,
  };
}

export function syncActiveScenario(scenarios, activeScenarioId, inputs, assumptions) {
  if (!Array.isArray(scenarios) || scenarios.length === 0) return scenarios;
  return scenarios.map((s) => (
    s.id === activeScenarioId
      ? { ...s, inputs: deepClone(inputs) || {}, assumptions: deepClone(assumptions) || {} }
      : s
  ));
}

export function nextOrdinal(scenarios) {
  if (!Array.isArray(scenarios) || scenarios.length === 0) return 1;
  return Math.max(0, ...scenarios.map((s) => Number(s.ordinal) || 0)) + 1;
}

export function uniqueCopyName(scenarios, sourceName) {
  const base = `Copy of ${normalizeName(sourceName)}`;
  const names = new Set((scenarios || []).map((s) => s.name));
  if (!names.has(base)) return base;
  let n = 2;
  while (names.has(`${base} ${n}`)) n += 1;
  return `${base} ${n}`;
}

export function duplicateScenario(scenarios, sourceId, name) {
  const list = Array.isArray(scenarios) ? scenarios : [];
  const source = list.find((s) => s.id === sourceId) || list[list.length - 1];
  if (!source) {
    const created = createScenario({ name, inputs: {}, assumptions: {}, ordinal: 1 });
    return { scenarios: [created], newScenario: created };
  }
  const copy = createScenario({
    name: name || uniqueCopyName(list, source.name),
    inputs: source.inputs,
    assumptions: source.assumptions,
    ordinal: nextOrdinal(list),
  });
  return { scenarios: [...list, copy], newScenario: copy };
}

export function renameScenario(scenarios, id, name) {
  const trimmed = normalizeName(name, '');
  if (!trimmed) return scenarios;
  return (scenarios || []).map((s) => (s.id === id ? { ...s, name: trimmed } : s));
}

export function deleteScenario(scenarios, activeScenarioId, id) {
  const list = Array.isArray(scenarios) ? scenarios : [];
  if (list.length <= 1) {
    return { scenarios: list, activeScenarioId, deleted: false };
  }
  const next = list.filter((s) => s.id !== id);
  if (next.length === list.length) {
    return { scenarios: list, activeScenarioId, deleted: false };
  }
  const nextActive = id === activeScenarioId
    ? (preferredScenario(next)?.id || next[0].id)
    : activeScenarioId;
  return { scenarios: next, activeScenarioId: nextActive, deleted: true };
}

/**
 * Open a version without copying deal-level FX onto it.
 * Keep the version's own snapshot. Only fill a missing snapshot when the
 * version is already frozen/manual (legacy hole) — never freeze a live version.
 */
export function resolveVersionAssumptionsOnOpen(versionAssumptions, dealFxSnapshot) {
  const next = { ...(versionAssumptions || {}), reportingCurrency: 'CHF' };
  if (next.fxSnapshot && Number(next.fxSnapshot.rate) > 0) {
    return next;
  }
  const mode = next.fxMode || 'live';
  if ((mode === 'frozen' || mode === 'manual') && dealFxSnapshot && Number(dealFxSnapshot.rate) > 0) {
    return { ...next, fxMode: mode, fxSnapshot: dealFxSnapshot };
  }
  return next;
}

/** Persistable slice stored on the same deal/file record. */
export function scenariosPersistSlice(scenarios, activeScenarioId, inputs, assumptions) {
  const synced = syncActiveScenario(scenarios, activeScenarioId, inputs, assumptions);
  const active = getActiveScenario(synced, activeScenarioId);
  return {
    scenarios: synced,
    activeScenarioId: active?.id || activeScenarioId,
  };
}
